import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    CircularProgress,
    Grid,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useLazyGetPldSearchResultsQuery } from "@services/api/pld";

import { useEffect } from "react";
import { RootState } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { setReloadSection, setResultsPldReady } from "@store/slices/componentsSlice";

export const ResultadosPld = () => {
    const dispatch = useDispatch();

    const applicationSelected = useSelector((state: RootState) => state.app.applicationSelected);
    const reloadSection = useSelector((state: RootState) => state.components.currentSection.reload);
    const printingProfileReport = useSelector((state: RootState) => state.components.printingProfileReport);

    const [triggerGetPldSearchResults, getPldSearchResults] = useLazyGetPldSearchResultsQuery();

    const getData = (field: any) => {
        try {
            JSON.parse(field);
            return JSON.stringify(field);
        } catch (e) {
            return field;
        }
    };

    const getColorByResult = (result: string) => {
        switch (result) {
            case "ABORTIVE":
                return "secondary10";
            case "REVIEW":
                return "main4";
            case "WITHOUT_REVIEW":
                return "secondary4";
        }
    };

    const reloadInfo = async () => {
        try {
            await triggerGetPldSearchResults(applicationSelected.id, false).unwrap();
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (reloadSection) {
            reloadInfo().finally(() => dispatch(setReloadSection(false)));
        }
    }, [reloadSection]);

    useEffect(() => {
        if (getPldSearchResults.isSuccess) {
            if (printingProfileReport.printingMode) {
                dispatch(setResultsPldReady(true));
            }
        }
    }, [getPldSearchResults]);

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0" || applicationSelected.id === undefined) return;

        reloadInfo();
    }, [applicationSelected.id]);

    return (
        <Stack>
            <Grid container>
                <Grid size={4}>
                    <Typography variant="body1" fontWeight={700} color="main3" textAlign={"center"}>
                        Rol
                    </Typography>
                </Grid>
                <Grid size={4}>
                    <Typography variant="body1" fontWeight={700} color="main3" textAlign={"center"}>
                        Rfc
                    </Typography>
                </Grid>
                <Grid size={"grow"}>
                    <Typography variant="body1" fontWeight={700} color="main3" textAlign={"center"}>
                        Persona/Empresa
                    </Typography>
                </Grid>
            </Grid>
            {getPldSearchResults.isSuccess && Array.isArray(getPldSearchResults.currentData) && (
                <>
                    {getPldSearchResults.currentData
                        ?.filter((t) => t.provider === "QUIEN_ES_QUIEN")
                        .map((person, idx) => (
                            <Accordion key={idx} defaultExpanded={printingProfileReport.printingMode}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Grid container width={"100%"} size={12}>
                                        <Grid size={4}>
                                            <Typography>{person.person_type.name}</Typography>
                                        </Grid>
                                        <Grid size={4} textAlign={"center"}>
                                            <Typography>{person.rfc}</Typography>
                                        </Grid>
                                        <Grid size={"grow"}>
                                            <Typography>{person.name}</Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {person.results.map((result, idx) => (
                                        <Accordion key={idx} defaultExpanded={printingProfileReport.printingMode}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Grid container columnGap={2}>
                                                    <Grid>
                                                        <Typography component={"span"} variant="body1" color="main3">
                                                            Proveedor:{" "}
                                                            <Typography
                                                                component={"span"}
                                                                variant="body1"
                                                                color="main3"
                                                                fontWeight={500}
                                                            >
                                                                {person.provider}
                                                            </Typography>
                                                        </Typography>
                                                    </Grid>
                                                    <Grid>
                                                        <Typography component={"span"} variant="body1" color="main3">
                                                            Lista:{" "}
                                                            <Typography
                                                                component={"span"}
                                                                variant="body1"
                                                                color="main3"
                                                                fontWeight={500}
                                                            >
                                                                {result.LISTA}
                                                            </Typography>
                                                        </Typography>
                                                    </Grid>
                                                    <Grid>
                                                        <Typography component={"span"} variant="body1" color="main3">
                                                            Coincidencia:{" "}
                                                            <Typography
                                                                component={"span"}
                                                                variant="body1"
                                                                fontWeight={500}
                                                            >
                                                                {result.COINCIDENCIA} %
                                                            </Typography>
                                                        </Typography>
                                                    </Grid>
                                                    <Grid>
                                                        <Typography component={"span"} variant="body1" color="main3">
                                                            Resultado:{" "}
                                                            <Typography
                                                                component={"span"}
                                                                variant="body1"
                                                                color={getColorByResult(result.result_)}
                                                                fontWeight={500}
                                                            >
                                                                {result.result_}
                                                            </Typography>
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <TableContainer>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Columna</TableCell>
                                                                <TableCell>Valor</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {Object.keys(result)
                                                                .filter(
                                                                    (header) =>
                                                                        [
                                                                            "LISTA",
                                                                            "COINCIDENCIA",
                                                                            "_conditions",
                                                                            "result_",
                                                                            "_type",
                                                                        ].some((t) => t === header) === false,
                                                                )
                                                                .map((item, idx) => (
                                                                    <TableRow key={idx}>
                                                                        <TableCell>{item}</TableCell>
                                                                        <TableCell>{getData(result[item])}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                    {person.results.length === 0 && (
                                        <Typography textAlign={"center"}>Sin coincidencias</Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    {getPldSearchResults.currentData
                        ?.filter((t) => t.provider === "METAMAP")
                        .map((person, idx) => (
                            <Accordion key={idx} defaultExpanded={printingProfileReport.printingMode}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Grid container width={"100%"} size={12}>
                                        <Grid size={4}>
                                            <Typography>{person.person_type.name}</Typography>
                                        </Grid>
                                        <Grid size={4}>
                                            <Typography textAlign={"center"}>{person.rfc}</Typography>
                                        </Grid>
                                        <Grid size={"grow"}>
                                            <Typography>{person.name}</Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Accordion key={idx} defaultExpanded={printingProfileReport.printingMode}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Grid container columnGap={2}>
                                                <Grid>
                                                    <Typography component={"span"} variant="body1" color="main3">
                                                        Proveedor:{" "}
                                                        <Typography
                                                            component={"span"}
                                                            variant="body1"
                                                            color="main3"
                                                            fontWeight={500}
                                                        >
                                                            {person.provider}
                                                        </Typography>
                                                    </Typography>
                                                </Grid>
                                                <Grid>
                                                    {person.results.code !== undefined ? (
                                                        <>
                                                            <Typography
                                                                component={"span"}
                                                                variant="body1"
                                                                color="main3"
                                                                mr={1}
                                                            >
                                                                Codigo: {person.results.code}
                                                            </Typography>
                                                            <Typography
                                                                component={"span"}
                                                                variant="body1"
                                                                color="main3"
                                                            >
                                                                Mensaje: {person.results.message}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography component={"span"} variant="body1" color="main3">
                                                            Perfil:{" "}
                                                            <Link target="_blank" href={person.results.profileUrl}>
                                                                {person.results.nameSearched}
                                                            </Link>
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Columna</TableCell>
                                                            <TableCell>Valor</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {person.results?.details && (
                                                            <>
                                                                {Object.keys(person.results.details)
                                                                    .filter(
                                                                        (header) =>
                                                                            [
                                                                                "LISTA",
                                                                                "COINCIDENCIA",
                                                                                "hits",
                                                                                "labels",
                                                                                "blacklistHits",
                                                                            ].some((t) => t === header) === false,
                                                                    )
                                                                    .map((item, idx) => (
                                                                        <TableRow key={idx}>
                                                                            <TableCell>{item}</TableCell>
                                                                            <TableCell>
                                                                                {person.results.details[item]}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                            </>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                </>
            )}

            {getPldSearchResults.isSuccess && getPldSearchResults.currentData?.length === 0 && (
                <Typography textAlign={"center"}>Sin coincidencias</Typography>
            )}
            {getPldSearchResults.isLoading && (
                <Grid container size={12} sx={{ justifyContent: "center" }}>
                    <CircularProgress />
                </Grid>
            )}
        </Stack>
    );
};
